// The staff-facing vocabulary for the two place-card content fields — Apple Maps' "Good to Know"
// (`locationAttributes`) and "About the place" (`locationDescriptions`).
//
// ⚠️ THIS FILE IS A MIRROR, AND THE BACKEND IS THE SOURCE OF TRUTH. The real list is
// `functions/src/schemas/apple-location-attributes.generated.ts` in `apple-integration-backend`,
// which is GENERATED from Apple's published `location-attributes-2026-07-14.csv`. These entries were
// produced from that module rather than typed, and every label below is Apple's own copy.
//
// ⚠️ DRIFT HERE FAILS SAFE, which is why a mirror is acceptable across two repos. The backend
// re-validates every path against the generated vocabulary before it calls Apple, and refuses an
// unknown one with a named reason and NO Apple call. So a stale option in this list produces an
// actionable rejection on the step, never a bad write to a client's place card.
//
// To grow the list: add the path in the backend's generator, re-run
// `pnpm cli:generate-location-attributes`, then update this file to match.

/** One "Good to Know" amenity a staff member can tick. */
export interface AppleLocationAttributeOption {
  /** Apple's dotted attribute path. Sent verbatim as the payload's `name`. */
  id: string;
  /** Apple's own label, prefixed with its group so the flat multi-select reads as grouped. */
  label: string;
  /** Apple's own description, shown as help text — its copy is better than ours and client-safe. */
  description: string;
}

/**
 * The 28 attributes offered to staff, ordered by group.
 *
 * ⚠️ Ticking a box asserts the amenity as `"true"`. Leaving one unticked asserts NOTHING — it does
 * not tell Apple the practice lacks it (stakeholder decision, 2026-08-31). That is why there is no
 * "no" option: an un-filled form must not publish a negative accessibility claim about a real clinic.
 */
export const APPLE_LOCATION_ATTRIBUTE_OPTIONS: AppleLocationAttributeOption[] = [
  {
    id: "crossbusiness.accessibility_features.wheelchair_accessible",
    label: "Accessibility — Wheelchair Accessible",
    description:
      "Wheelchair Accessible means this business or location can be accessed and navigated by guests using a wheelchair.",
  },
  {
    id: "crossbusiness.accessibility_features.step_free_access",
    label: "Accessibility — Step-Free Access",
    description:
      "Step-Free Access means this business is accessible via ramps or elevators, allowing guests to move between levels without using stairs or escalators.",
  },
  {
    id: "crossbusiness.accessibility_features.accessible_elevator",
    label: "Accessibility — Accessible Elevator",
    description:
      "An Accessible Elevator is an elevator equipped with features to assist guests with visual, hearing, or physical impairments.",
  },
  {
    id: "crossbusiness.accessibility_features.digital_accessibility",
    label: "Accessibility — Digital Accessibility",
    description:
      "Digital Accessibility refers to digital features that support guests with visual, hearing, or physical impairments.",
  },
  {
    id: "crossbusiness.restrooms.restroom",
    label: "Restrooms — Restrooms",
    description:
      "A Restroom is a bathroom or toilet facility available to guests at a business's premises.",
  },
  {
    id: "crossbusiness.restrooms.accessible_restroom",
    label: "Restrooms — Accessible Restrooms",
    description:
      "Accessible Restrooms means the bathroom facilities are equipped to accommodate guests with disabilities, including features such as grab bars, wider doorways, and other accessibility aids.",
  },
  {
    id: "crossbusiness.restrooms.gender_neutral_restroom",
    label: "Restrooms — Gender-Neutral Restrooms",
    description:
      "Gender-Neutral Restrooms refers to a bathroom facility that can be used by any guest, regardless of their gender.",
  },
  {
    id: "crossbusiness.restrooms.family_restroom",
    label: "Restrooms — Family Restrooms with Changing Stations",
    description:
      "Family Restrooms or Family Restrooms with Changing Station refers to a restroom designed for families, equipped with amenities such as a baby changing station, a feeding area, and child-sized toilet facilities.",
  },
  {
    id: "crossbusiness.parking.accessibleparking",
    label: "Parking — Accessible Parking",
    description:
      "Accessible Parking means this business has a parking facility equipped with accessibility features such as ramps and lifts to support guests with mobility needs.",
  },
  {
    id: "crossbusiness.parking.free_selfpark",
    label: "Parking — Free Self-Parking",
    description:
      "Free Self-Parking means guests can park their own vehicle at this establishment at no cost.",
  },
  {
    id: "crossbusiness.parking.parking_lot",
    label: "Parking — Parking Lot",
    description: "A Parking Lot is a dedicated outdoor area for parking vehicles.",
  },
  {
    id: "crossbusiness.parking.streetparking",
    label: "Parking — Street Parking",
    description:
      "Street Parking means vehicles can be parked on or along the curb of the street near this business.",
  },
  {
    id: "crossbusiness.parking.parking_validated",
    label: "Parking — Parking Validated",
    description:
      "Parking Validated means this business will validate a guest's parking ticket to reduce or eliminate the parking fee.",
  },
  {
    id: "crossbusiness.parking.parking_garage",
    label: "Parking — Parking Garage",
    description:
      "A Parking Garage is a covered multi-level building provided by a business where guests can park their vehicles.",
  },
  {
    id: "crossbusiness.parking.bike_parking",
    label: "Parking — Bike Parking",
    description:
      "Bike Parking refers to a dedicated area at this business where guests can park their bicycles.",
  },
  {
    id: "crossbusiness.payments.applepay",
    label: "Payments — Accepts Apple Pay",
    description:
      "Accepts Apple Pay means this business accepts payments made through Apple Pay using an Apple device.",
  },
  {
    id: "crossbusiness.payments.contactless_pay",
    label: "Payments — Accepts Contactless Payments",
    description:
      "Accepts Contactless Payments means this business accepts contactless payment methods, where guests can pay by tapping their card or device on the payment terminal without physical contact.",
  },
  {
    id: "crossbusiness.payments.creditcard",
    label: "Payments — Accepts Credit Cards",
    description:
      "Accepts Credit Cards means this business accepts credit cards as a payment method.",
  },
  {
    id: "crossbusiness.payments.debitcard",
    label: "Payments — Accepts Debit Cards",
    description: "Accepts Debit Cards means this business accepts debit cards as a payment method.",
  },
  {
    id: "crossbusiness.payments.cash",
    label: "Payments — Accepts Cash",
    description: "Accepts Cash means this business accepts cash as a payment method.",
  },
  {
    id: "crossbusiness.spoken_languages.english",
    label: "Languages spoken — English Spoken",
    description:
      "English Spoken means the staff at this business can communicate with guests in English.",
  },
  {
    id: "crossbusiness.spoken_languages.spanish",
    label: "Languages spoken — Spanish Spoken",
    description:
      "Spanish Spoken means the staff at this business can communicate with guests in Spanish.",
  },
  {
    id: "crossbusiness.family_kids.kids_playarea",
    label: "Family & kids — Kids' Play Area",
    description:
      "Kids' Play Area refers to a dedicated area within a business where children can play.",
  },
  {
    id: "crossbusiness.family_kids.parents_waitingarea",
    label: "Family & kids — Parent Waiting Area",
    description:
      "Parent Waiting Area refers to a designated seating area where parents can wait while their children attend a class or activity.",
  },
  {
    id: "crossbusiness.safety_features.air_purifier",
    label: "Health & safety — Air Purifiers",
    description:
      "Air Purifiers refers to the air purification systems a business uses to maintain clean, high-quality indoor air as a health and safety measure.",
  },
  {
    id: "crossbusiness.safety_features.free_sanitizers",
    label: "Health & safety — Free Hand Sanitizers",
    description:
      "Free Hand Sanitizers means this business provides complimentary hand sanitizers to guests.",
  },
  {
    id: "crossbusiness.safety_features.sanitationstation",
    label: "Health & safety — Sanitation Stations",
    description:
      "Sanitation Stations refers to designated areas or booths at a business where guests can sanitize their hands.",
  },
  {
    id: "crossbusiness.techfeatures.airconditioning",
    label: "Comfort — Air Conditioning",
    description:
      "Air Conditioning means this business provides air conditioning to maintain a cool and comfortable indoor environment for guests.",
  },
];

/**
 * `enumValues` for the multi-select, in the same order.
 *
 * FireCMS has no grouped-checkbox control, so the grouping is carried in the label text
 * ("Parking — Bike Parking") and by the array order, which it preserves.
 */
export const APPLE_LOCATION_ATTRIBUTE_ENUM_VALUES = APPLE_LOCATION_ATTRIBUTE_OPTIONS.map(
  ({ id, label }) => ({ id, label }),
);

/** Apple's bounds for the "About the place" text (`AppleDataSpecificationReference.rtf:148`). */
export const ABOUT_TEXT_MIN_LENGTH = 5;
export const ABOUT_TEXT_MAX_LENGTH = 500;
